import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  List,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  AddCircleOutline,
  Archive,
  CalendarToday,
  DriveFileRenameOutline,
  ExpandMore,
  Groups,
  Payments,
  PersonRemove,
  Receipt,
} from "@mui/icons-material";
import type { Club, ClubMember, TrainingGroup, UpcomingTrainingDate } from "./types";
import { getTrainingGroupId } from "./types";
import MemberListItem from "./MemberListItem";
import UpcomingSessionCard from "./UpcomingSessionCard";

interface TrainingGroupSectionProps {
  club: Club;
  group: TrainingGroup;
  expanded: boolean;
  onExpandChange: (expanded: boolean) => void;
  onAddMember: (club: Club, group: TrainingGroup) => void;
  onIssueInvoice: (club: Club, group: TrainingGroup) => void;
  onRename: (club: Club, group: TrainingGroup) => void;
  onArchive: (group: TrainingGroup) => void;
  onSetDefaultFee: (club: Club, group: TrainingGroup) => void;
  onRemoveMember: (group: TrainingGroup, member: ClubMember) => void;
  onEditNotes: (group: TrainingGroup, date: UpcomingTrainingDate) => void;
  containerRef?: React.Ref<HTMLDivElement>;
}

const TrainingGroupSection: React.FC<TrainingGroupSectionProps> = ({
  club,
  group,
  expanded,
  onExpandChange,
  onAddMember,
  onIssueInvoice,
  onRename,
  onArchive,
  onSetDefaultFee,
  onRemoveMember,
  onEditNotes,
  containerRef,
}) => {
  const theme = useTheme();
  const groupIdAvailable = getTrainingGroupId(group) != null;
  const defaultFee = group.default_fee ?? 0;
  const defaultFeeBasis = group.default_fee_basis ?? "invoice";
  const defaultFeeLabel =
    defaultFee === 0
      ? "Default Fee"
      : defaultFeeBasis === "session"
        ? `Default Fee: £${defaultFee.toFixed(2)} / session`
        : `Default Fee: £${defaultFee.toFixed(2)} / invoice`;

  return (
    <Box ref={containerRef} sx={{ mt: 2 }}>
      <Accordion
        expanded={expanded}
        onChange={(_, isExpanded) => onExpandChange(isExpanded)}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            bgcolor: theme.palette.success.light + "10",
            borderRadius: 1,
            mb: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              gap: 1,
              pr: { xs: 0.5, sm: 2 },
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                minWidth: 0,
                flex: 1,
              }}
            >
              <Groups sx={{ flexShrink: 0 }} />
              <Typography
                sx={{
                  fontWeight: 600,
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                }}
              >
                {group.group_name}
              </Typography>
            </Box>
            <Chip
              label={`${group.members.length} members`}
              size="small"
              color="success"
              sx={{ flexShrink: 0 }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: { xs: 1, sm: 2 } }}>
          <Box sx={{ mb: 2 }}>
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              sx={{
                "& > .MuiButton-root": {
                  flex: { xs: "1 1 calc(50% - 4px)", sm: "0 0 auto" },
                  minWidth: 0,
                },
              }}
            >
              <Button
                variant="outlined"
                startIcon={<AddCircleOutline />}
                size="small"
                disabled={!groupIdAvailable}
                onClick={() => onAddMember(club, group)}
              >
                Add Member
              </Button>
              <Button
                variant="outlined"
                startIcon={<Receipt />}
                size="small"
                color="secondary"
                onClick={() => onIssueInvoice(club, group)}
              >
                Issue Invoice
              </Button>
              <Button
                variant="outlined"
                startIcon={<Payments />}
                size="small"
                disabled={!groupIdAvailable}
                onClick={() => onSetDefaultFee(club, group)}
              >
                {defaultFeeLabel}
              </Button>
              <Button
                variant="outlined"
                startIcon={<DriveFileRenameOutline />}
                size="small"
                disabled={!groupIdAvailable}
                onClick={() => onRename(club, group)}
              >
                Rename
              </Button>
              <Button
                variant="outlined"
                startIcon={<Archive />}
                size="small"
                color="warning"
                disabled={!groupIdAvailable}
                onClick={() => onArchive(group)}
              >
                Archive
              </Button>
            </Stack>
          </Box>

          {group.upcoming_training_dates &&
            group.upcoming_training_dates.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <CalendarToday
                    fontSize="small"
                    sx={{ color: theme.palette.info.main }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Upcoming Sessions
                  </Typography>
                </Box>
                <Grid container spacing={1.5}>
                  {group.upcoming_training_dates.map((date, idx) => (
                    <Grid item xs={12} sm={6} md={4} key={idx}>
                      <UpcomingSessionCard
                        date={date}
                        onEditNotes={(d) => onEditNotes(group, d)}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

          <List>
            {group.members?.map((member, index) => (
              <React.Fragment key={member.handler_id}>
                <MemberListItem
                  member={member}
                  avatarColor={theme.palette.success.main}
                  action={
                    groupIdAvailable ? (
                      <IconButton
                        edge="end"
                        size="small"
                        color="error"
                        aria-label={`Remove ${member.handler_name} from group`}
                        onClick={() => onRemoveMember(group, member)}
                      >
                        <PersonRemove fontSize="small" />
                      </IconButton>
                    ) : undefined
                  }
                />
                {index < group.members.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default TrainingGroupSection;
